/** @jsx jsx */
import { useContext, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { useFormikContext } from 'formik';

import { IChallengesPartial, ICustomTheme } from 'interfaces';

import { useMediaQuery, useTheme } from '@material-ui/core';
import { DeclarationContext } from 'components/DeclarationForm';
import {
  IForm,
  IFormProduct,
  StepsType,
} from 'components/DeclarationForm/interfaces';
import { STEPS } from 'components/DeclarationForm/constants';
import {
  StyledNavButton,
  StyledContainer,
  StyledArrowBackIcon,
  StyledDoneIcon,
  StyledArrowForwardIcon,
} from './styles';

export const NavButtons = ({
  step,
  selectedProducts,
  scrollTop,
  challengeData,
}: {
  step: StepsType;
  selectedProducts: IFormProduct[];
  scrollTop?: () => void;
  challengeData: IChallengesPartial;
}) => {
  const {
    isFirstStep,
    goPrevStep,
    goNextStep,
    setOpenDeclarationConfirmationDialog,
  } = useContext(DeclarationContext);
  const { touched, errors } = useFormikContext<IForm>();

  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasQuizQuestions = challengeData?.declarationForm;
  const distributorData = challengeData?.predefinedQuestions?.find(
    (item) => item.key === 'distributor',
  );
  const invoiceDateStartData = challengeData?.predefinedQuestions?.find(
    (item) => item.key === 'invoiceDateStart',
  );
  const invoiceDateEndData = challengeData?.predefinedQuestions?.find(
    (item) => item.key === 'invoiceDateEnd',
  );

  let isStepTouched: boolean | undefined = false;
  if (step === STEPS.summary) {
    isStepTouched = true;
  }
  if (step === STEPS.answers) {
    const checks = [
      ...(hasQuizQuestions ? ['answers'] : []),
      ...(distributorData?.isDisplayed ? ['distributor'] : []),
      ...(invoiceDateStartData?.isDisplayed ? ['invoiceDateStart'] : []),
      ...(invoiceDateEndData?.isDisplayed ? ['invoiceDateEnd'] : []),
    ];
    isStepTouched = checks.every((key) => touched[key as keyof typeof touched]);
  }

  if (step !== STEPS.answers && step !== STEPS.summary) {
    isStepTouched = !!touched[step];
  }

  const isStepErrored = useMemo(() => {
    if (step === STEPS.summary) {
      return false;
    }
    if (step === STEPS.answers) {
      const checks = [
        ...(hasQuizQuestions ? ['answers'] : []),
        ...(distributorData?.isDisplayed ? ['distributor'] : []),
        ...(invoiceDateStartData?.isDisplayed ? ['invoiceDateStart'] : []),
        ...(invoiceDateEndData?.isDisplayed ? ['invoiceDateEnd'] : []),
      ];
      const isErrored = checks.some(
        (key) => errors?.[key as keyof typeof touched],
      );
      return isErrored;
    }

    if (step !== STEPS.products) {
      return !!errors[step];
    }

    return !selectedProducts?.length || !!errors[step];
  }, [errors, step, selectedProducts]);

  const resolveScrollTop = (scrollTop: () => void) => {
    return new Promise((resolve) => {
      resolve(scrollTop());
    });
  };

  const handleChangeStepWithScrollTop = async (
    scrollTop: (() => void) | undefined,
    gotToStepAction: () => void,
  ) => {
    scrollTop && (await resolveScrollTop(scrollTop));
    return gotToStepAction();
  };

  return (
    <StyledContainer isMobile={isMobile}>
      {!isFirstStep && (
        <StyledNavButton
          onClick={() => handleChangeStepWithScrollTop(scrollTop, goPrevStep)}
          variant="contained"
          type="button"
          disableElevation
          backgroundColor={theme.colors.grey.main}
          isMobile={isMobile}
        >
          <StyledArrowBackIcon />
        </StyledNavButton>
      )}
      <StyledNavButton
        disabled={!isStepTouched || isStepErrored}
        onClick={() =>
          step === STEPS.summary
            ? setOpenDeclarationConfirmationDialog(true)
            : handleChangeStepWithScrollTop(scrollTop, goNextStep)}
        variant="contained"
        type="button"
        disableElevation
        backgroundColor={theme.colors.paletteFromBO.primary}
        isMobile={isMobile}
      >
        {step === STEPS.summary ? (
          <StyledDoneIcon />
        ) : (
          <StyledArrowForwardIcon />
        )}
      </StyledNavButton>
    </StyledContainer>
  );
};
